import React from "react"
import { useState } from "react"
import { X, ImagePlus, Trash2 } from "lucide-react"
import { motion } from "framer-motion"
import styles from "./ProductPublicationModal.module.scss"
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import { fireAnalytics } from "../../../utils/firebase"
import { getDeviceCategory, getSessionId } from "../../../utils/analyticsHelpers.ts"

const modal = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 },
  },
}

interface ProductData {
  name: string
  description: string
  startDate: string
  endDate: string
  zone: string
  images: File[]
  type: string
  priceUnit: string
  price: string
  currencyType: string
}

interface ProductPublicationModalProps {
  onClose: () => void
  onBack: () => void
  onPublish: (data: ProductData) => void
  userData?: {
    id?: number
    image_profile?: string
    name?: string
    last_name?: string
    razon_social?: string
    type?: string
  } | null
}

export default function ProductPublicationModal({
  onClose,
  onBack,
  onPublish,
  userData,
}: ProductPublicationModalProps) {
  const [formData, setFormData] = useState<ProductData>({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    zone: "",
    images: [],
    type: "",
    priceUnit: "",
    price: "",
    currencyType: "",
  })

  const fullName = userData?.type === "COMPANY" ? userData.razon_social : `${userData?.name ?? ""} ${userData?.last_name ?? ""}`.trim() || ""
  const { guard } = useActionGuard()
  const handleInputChange = (field: keyof ProductData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (formData.images.length + files.length > 5) {
      alert("No se pueden agregar más de 5 imágenes")
      return
    }
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }))
  }

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }))
  }

  const isFormValid = () => {
    return (
      formData.name &&
      formData.description &&
      formData.startDate &&
      formData.endDate &&
      formData.zone &&
      formData.images.length > 0 &&
      formData.type
    )
  }

  const handlePublish = guard("create_product", () => {
    if (isFormValid()) {
      onPublish(formData)
    }
  }, () => {
    fireAnalytics("product_created", {
      edify_user_id: userData?.id || null,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "publishing",
      action: "product_created",
      product_id: userData?.id || null,
    })
  })

    return (
      <ModalOverlay isOpen={true} onClose={onClose}>
        <motion.div
          variants={modal}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={styles.modal}
        >
          <div className={styles.header}>
            <div className={styles.header_content}>
              <div className={styles.header_userInfo}>
                <img
                  src={userData?.image_profile || "/placeholder.svg?height=40&width=40"}
                  alt="profile"
                  className={styles.header_avatar}
                />
                <div>
                  <div className={styles.header_name}>{fullName}</div>
                  <div className={styles.header_subtitle}>Crear producto o servicio</div>
                </div>
              </div>
              <div className={styles.header_actions}>
                <button className={styles.header_button} onClick={onBack}>
                  ←
                </button>
                <button className={styles.header_button} onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className={styles.content}>
            <div className={styles.imageSection}>
              {formData.images.length > 0 ? (
                <div className={styles.imageSection_grid}>
                  {formData.images.map((image, index) => (
                    <div key={index} className={styles.imageSection_item}>
                      <img
                        src={URL.createObjectURL(image) || "/placeholder.svg"}
                        alt={`Preview ${index + 1}`}
                        className={styles.imageSection_image}
                      />
                      <button className={styles.imageSection_removeButton} onClick={() => removeImage(index)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  {formData.images.length < 5 && (
                    <label className={styles.imageSection_addMore}>
                      <ImagePlus size={24} color="rgb(107, 114, 128)" />
                      <span>Agregar más</span>
                      <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                    </label>
                  )}
                </div>
              ) : (
                <label className={styles.imageSection_empty}>
                  <ImagePlus size={48} color="rgb(156, 163, 175)" />
                  <div className={styles.imageSection_empty_title}>Agrega imágenes de tu producto o servicio</div>
                  <div className={styles.imageSection_empty_subtitle}>JPG, PNG hasta 5MB</div>
                  <div className={styles.imageSection_empty_button}>Seleccionar archivos</div>
                  <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                </label>
              )}
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label htmlFor="name">Nombre *</label>
                <input
                  id="name"
                  type="text"
                  placeholder="Por ejemplo: Instalación de A/A"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
              </div>

              <div className={styles.formField}>
                <label htmlFor="type">Tipo *</label>
                <select id="type" value={formData.type} onChange={(e) => handleInputChange("type", e.target.value)}>
                  <option value="">Producto/servicio</option>
                  <option value="producto">Producto</option>
                  <option value="servicio">Servicio</option>
                </select>
              </div>

              <div className={styles.formField}>
                <label htmlFor="zone">Ubicación *</label>
                <input
                  id="zone"
                  type="text"
                  placeholder="Pilar, Buenos Aires"
                  value={formData.zone}
                  onChange={(e) => handleInputChange("zone", e.target.value)}
                />
              </div>

              <div className={styles.formField}>
                <label htmlFor="currencyType">Moneda</label>
                <select
                  id="currencyType"
                  value={formData.currencyType}
                  onChange={(e) => handleInputChange("currencyType", e.target.value)}
                >
                  <option value="">ARS/USD/EUR</option>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div className={styles.formField}>
                <label htmlFor="priceUnit">Precio por</label>
                <select
                  id="priceUnit"
                  value={formData.priceUnit}
                  onChange={(e) => handleInputChange("priceUnit", e.target.value)}
                >
                  <option value="">Precio por</option>
                  <option value="unidad">Unidad</option>
                  <option value="m">Metro</option>
                  <option value="m2">Metro cuadrado</option>
                  <option value="cm3">Centímetro cúbico</option>
                  <option value="hora">Hora</option>
                  <option value="trabajo completo">Trabajo completo</option>
                </select>
              </div>

              <div className={styles.formField}>
                <label htmlFor="price">Valor</label>
                <input
                  id="price"
                  type="number"
                  placeholder="Ej: 100"
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                />
              </div>

              <div className={styles.formField}>
                <label htmlFor="startDate">Inicio *</label>
                <input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange("startDate", e.target.value)}
                />
              </div>

              <div className={styles.formField}>
                <label htmlFor="endDate">Fin *</label>
                <input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  min={formData.startDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                />
              </div>

              <div className={`${styles.formField} ${styles.fullWidth}`}>
                <label htmlFor="description">Descripción *</label>
                <textarea
                  id="description"
                  placeholder="Ej: Instalación de 10 unidades de A/A en edificio de oficinas."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <button className={styles.cancelButton} onClick={onBack}>
              Cancelar
            </button>
            <button onClick={handlePublish} disabled={!isFormValid()} className={styles.publishButton}>
              Publicar
            </button>
          </div>
        </motion.div>
      </ModalOverlay>
  )
}
